/**
 * CollectionDetail
 *
 * Slide-up drawer showing full collection record details:
 * photo, field values, health contribution, status, promotion suggestion.
 */

import { useState, useEffect } from "react";
import {
	Drawer,
	Tag,
	Spin,
	Divider,
	Button,
	message,
} from "antd";
import {
	CheckCircleOutlined,
	FlagOutlined,
	RocketOutlined,
	CloseCircleOutlined,
} from "@ant-design/icons";
import {
	getRecordById,
	reviewRecord,
	type CollectionRecord,
} from "@/services/data-collection-service";
import "./CollectionDetail.scss";

interface CollectionDetailProps {
	open: boolean;
	recordId: number;
	onClose: () => void;
	onUpdated: () => void;
}

export const CollectionDetail = ({
	open,
	recordId,
	onClose,
	onUpdated,
}: CollectionDetailProps) => {
	const [record, setRecord] = useState<CollectionRecord | null>(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		if (!open || !recordId) return;
		setLoading(true);
		getRecordById(recordId)
			.then(setRecord)
			.catch(() => setRecord(null))
			.finally(() => setLoading(false));
	}, [open, recordId]);

	const handleReview = async () => {
		try {
			await reviewRecord(recordId);
			message.success("Record marked as reviewed");
			onUpdated();
			const updated = await getRecordById(recordId);
			setRecord(updated);
		} catch {
			message.error("Failed to mark as reviewed");
		}
	};

	const getStatusColor = (status: string) => {
		switch (status) {
			case "COMPLETED": return "success";
			case "REVIEWED": return "blue";
			case "PROCESSING": return "processing";
			case "FLAGGED": return "error";
			case "PENDING": return "default";
			default: return "default";
		}
	};

	const getHealthColor = (score: number) => {
		if (score >= 80) return "#52c41a";
		if (score >= 50) return "#faad14";
		return "#ff4d4f";
	};

	// Render field values from finalData or aiExtractedData
	const renderFieldValues = () => {
		const data = record?.finalData ?? {};
		const aiData = record?.aiExtractedData ?? {};

		// Merge keys from both sources
		const allKeys = new Set([...Object.keys(data), ...Object.keys(aiData)]);

		if (allKeys.size === 0) {
			return <div className="cd-empty-fields">No field data captured</div>;
		}

		return (
			<div className="cd-field-grid">
				{Array.from(allKeys).map((key) => {
					const finalVal = data[key];
					const aiVal = aiData[key];
					const displayValue = finalVal ?? (aiVal as any)?.value;
					const confidence = (aiVal as any)?.confidence;

					return (
						<div key={key} className="cd-field-row">
							<span className="cd-field-key">
								{key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
							</span>
							<span className="cd-field-value">
								{displayValue === true
									? "Yes"
									: displayValue === false
										? "No"
										: Array.isArray(displayValue)
											? displayValue.join(", ")
											: String(displayValue ?? "—")}
								{confidence !== undefined && !finalVal && (
									<span className="cd-confidence">
										{Math.round(confidence * 100)}%
									</span>
								)}
							</span>
						</div>
					);
				})}
			</div>
		);
	};

	return (
		<Drawer
			open={open}
			onClose={onClose}
			placement="bottom"
			height="85vh"
			title={record?.templateName || "Collection Record"}
			className="collection-detail-drawer"
			destroyOnClose
		>
			{loading ? (
				<div className="cd-loading"><Spin size="large" /></div>
			) : record ? (
				<div className="cd-content">
					{/* Status & Health */}
					<div className="cd-status-row">
						<Tag color={getStatusColor(record.status)}>
							{record.status}
						</Tag>
						{record.templateCategory && (
							<Tag color="purple">{record.templateCategory}</Tag>
						)}
						{record.healthScoreContribution !== null && record.healthScoreContribution !== undefined && (
							<div
								className="cd-health-pill"
								style={{ borderColor: getHealthColor(record.healthScoreContribution) }}
							>
								<span style={{ color: getHealthColor(record.healthScoreContribution) }}>
									{Math.round(record.healthScoreContribution)}
								</span>
								<span className="cd-health-label">health</span>
							</div>
						)}
					</div>

					{/* Location / Area */}
					<div className="cd-meta-row">
						{record.locationName && (
							<span className="cd-meta-item">{record.locationName}</span>
						)}
						{record.areaName && (
							<span className="cd-meta-item cd-area">{record.areaName}</span>
						)}
						{record.collectedByName && (
							<span className="cd-meta-item">by {record.collectedByName}</span>
						)}
						<span className="cd-meta-item">
							{new Date(record.createdAt).toLocaleDateString()}
						</span>
					</div>

					{/* Photo */}
					{record.primaryPhotoUrl && (
						<div className="cd-photo">
							<img src={record.primaryPhotoUrl} alt="Collection" />
						</div>
					)}

					<Divider style={{ margin: "16px 0", borderColor: "rgba(255,255,255,0.1)" }} />

					{/* Field Values */}
					<h4 className="cd-section-title">Collected Data</h4>
					{renderFieldValues()}

					{/* Notes */}
					{record.notes && (
						<>
							<Divider style={{ margin: "16px 0", borderColor: "rgba(255,255,255,0.1)" }} />
							<h4 className="cd-section-title">Notes</h4>
							<p className="cd-notes">{record.notes}</p>
						</>
					)}

					{/* Promotion Banner */}
					{record.assetPromotionStatus === "SUGGESTED" && (
						<div className="cd-promotion-banner">
							<RocketOutlined />
							<span>AI suggests this could be tracked as an asset</span>
						</div>
					)}

					{/* Actions */}
					<div className="cd-actions">
						{record.status === "COMPLETED" && (
							<Button
								type="primary"
								icon={<CheckCircleOutlined />}
								onClick={handleReview}
								block
							>
								Mark as Reviewed
							</Button>
						)}
					</div>

					{/* Additional photos */}
					{record.photos && record.photos.length > 0 && (
						<>
							<Divider style={{ margin: "16px 0", borderColor: "rgba(255,255,255,0.1)" }} />
							<h4 className="cd-section-title">Additional Photos</h4>
							<div className="cd-photo-grid">
								{record.photos.map((p) => (
									<div key={p.id} className="cd-photo-thumb">
										<img src={p.photoUrl} alt={p.caption || "Photo"} />
										{p.caption && <span className="cd-photo-caption">{p.caption}</span>}
									</div>
								))}
							</div>
						</>
					)}
				</div>
			) : (
				<div className="cd-empty">Record not found</div>
			)}
		</Drawer>
	);
};
